import { memo, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { DateRangePicker } from 'materialui-daterange-picker';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import TwAutocomplete from '../../../TwAutocomplete';
import ErrorTooltip from '../../../ErrorTooltip';
import DurationSearchInput from '../../DurationSearchInput';
import {} from // autocompleteRequest,
'../../../../store/slices/autocomplete';
import {
  cruiseSearchRequest,
  setCruiseSearchParam,
  setCruiseSearchObjFromQueryString,
  setCruiseSearchInputs,
  resetSearchResults,
} from '../../../../store/slices/tripSearch';
import setURLParams from '../../../../utils/setURLParams';
import {
  // AutocompleteType,
  defaultPassengerCity,
  PageLinks,
  SearchModes,
  SearchStatus,
} from '../../../../constants';
import FilterBar from '../../FilterBar';
import { usePickerClasses, useAutocompleteStyles } from '../styles';
import useSearchObjFromQueryString from '../../../../hooks/useSearchObjFromQueryString';
import { resetTrips } from '../../../../store/slices/trip';
import { resetAgentTips } from '../../../../store/slices/intelligence';
import { setScrollState } from '../../../../store/slices/scroll';
import checkDoEnableTripConfigDecoupling from '../../../../helpers/selectors/checkDoEnableTripConfigDecoupling';

const CruiseSearchbar = ({ variant, doShowAllFilters, doDisablePassengersInput }) => {
  const SEARCH_MAX_YEARS = 3;
  const router = useRouter();
  const { debug, pageName, searchMode: searchModeQuery } = router.query;
  const { track } = useAnalytics();
  const { destinationsAutocompleteResults } = useSelector((store) => store.autocomplete);
  const { isLoggedIn, defaultCity: userDefaultCity } = useSelector((store) => store.user);
  const searchStatus = useSelector((store) => store.tripSearch.searchStatus);
  const searchInputs = useSelector((store) => store.tripSearch.searchInputs);
  const { staterooms, cruise: searchFilter } = searchInputs;
  const tripConfirmation = useSelector((store) => store.trip.tripConfirmation);
  const selectedSearchMode = useSelector((store) => store.tripSearch.selectedSearchMode);
  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);

  const { fromDate, toDate, locations, filters = {} } = searchFilter;
  const [isDepartureRangeDatepickerOpen, setIsDepartureRangeDatepickerOpen] = useState(false);

  const minDate = new Date();
  const maxDate = new Date(new Date().setFullYear(minDate.getFullYear() + SEARCH_MAX_YEARS));
  const initialFormValidations = {
    departureRange: null,
    city: null,
  };
  const [formValidations, setFormValidations] = useState(initialFormValidations);
  const dispatch = useDispatch();
  const isChangeCruisePage =
    pageName === PageLinks.ChangeCruise.pageName || pageName === PageLinks.AddCruise.pageName;

  const autocompleteClasses = useAutocompleteStyles({
    doAddBorderInRoot: true,
    minHeight: '100%',
    noPaddingTop: true,
    bgColor: 'var(--searchInputBgColorHex)',
  });
  const datePickerClasses = usePickerClasses();

  const handleResetValidation = () => {
    setFormValidations(initialFormValidations);
  };

  const validateSearch = (obj, doShowValidationErrors = true) => {
    const validationObj = { ...initialFormValidations };
    let isValid = true;
    if (!obj || !obj.fromDate || !obj.toDate) {
      validationObj.departureRange = 'Departure range is required.';
      isValid = false;
    } else {
      const startDate = new Date(obj.fromDate);
      const endDate = new Date(obj.toDate);
      const differenceInDays = (endDate - startDate) / (1000 * 60 * 60 * 24);

      if (differenceInDays > 90) {
        validationObj.departureRange = 'The departure range should not exceed 90 days.';
        isValid = false;
      }
    }

    // if (!obj.filters || !obj.filters.city) {
    //   validationObj.city = 'Resident city is required.';
    //   isValid = false;
    // }

    if (doShowValidationErrors) setFormValidations(validationObj);

    return isValid;
  };

  const handleSearch = (event, doShowValidationErrors = true) => {
    if (event) event.preventDefault();
    dispatch(resetTrips({ doEnableTripConfigDecoupling }));
    dispatch(resetAgentTips());
    dispatch(setScrollState({ status: null }));
    // Redirect to home page on search
    if (router.pathname !== PageLinks.Home.url && !isChangeCruisePage) {
      router.push(PageLinks.Home.url);
    }
    // TODO: should this method be passed in
    // or should search results be handled through middleware?
    dispatch(
      setCruiseSearchParam({
        staterooms,
        ...searchFilter,
        page: 1,
        doReturnOnlyAddedUpdatedItineraries: debug === 'true',
      }),
    );
    if (validateSearch(searchFilter, doShowValidationErrors)) {
      if (!isChangeCruisePage) {
        setURLParams({ searchObj: searchInputs, searchedMode: SearchModes.CRUISE_TRIP, router });
      }
      dispatch(cruiseSearchRequest({ isChangeCruisePage }));
    }
    track('searchbar.handleCruiseSearch', {
      searchObj: JSON.stringify(searchFilter),
    });
  };

  const handleDepartureRangeClick = () => {
    track('searchbar.handleDepartureRangeClick', { open: isDepartureRangeDatepickerOpen });
    setIsDepartureRangeDatepickerOpen(!isDepartureRangeDatepickerOpen);
  };

  const handleDepartureRangeChange = (range) => {
    track('searchbar.handleDepartureRangeChange', { ...range });
    setIsDepartureRangeDatepickerOpen(false);
    dispatch(
      setCruiseSearchInputs({
        fromDate: dayjs(range.startDate).format('YYYY-MM-DD'),
        toDate: dayjs(range.endDate).format('YYYY-MM-DD'),
      }),
    );
  };

  const handleDestinationsChange = (locs) => {
    track('searchbar.handleDestinationsChange', { locations: locs.map((l) => l.label).join() });
    dispatch(
      setCruiseSearchInputs({
        locations: locs,
      }),
    );
  };

  const returnDefinedRanges = () => [];

  // useEffect(() => {
  //   if (!destinationsAutocompleteResults || !destinationsAutocompleteResults.length) {
  //     dispatch(
  //       autocompleteRequest({
  //         autocompleteTypes: AutocompleteType.DESTINATIONS,
  //         responseType: 'destinationsAutocompleteResults',
  //       }),
  //     );
  //   }
  //   // oxlint-disable-next-line react/exhaustive-deps
  // }, []);

  useSearchObjFromQueryString(
    (searchObjFromQueryString) => {
      if (searchStatus !== SearchStatus.INITIAL) return;
      if ((searchModeQuery || SearchModes.CRUISE_TRIP) === selectedSearchMode) {
        dispatch(setCruiseSearchObjFromQueryString(searchObjFromQueryString));
        if (validateSearch(searchObjFromQueryString, false)) {
          dispatch(cruiseSearchRequest({ isChangeCruisePage }));
        }
      } else {
        dispatch(resetSearchResults({ isLoggedIn }));
        handleSearch(null, false);
      }
    },
    isChangeCruisePage && tripConfirmation && tripConfirmation.trip.resultsPageUrl,
  );

  useEffect(() => {
    if (!filters.city) {
      dispatch(
        setCruiseSearchInputs({
          filters: { city: userDefaultCity || defaultPassengerCity },
        }),
      );
    }
  }, [dispatch, filters.city, userDefaultCity]);

  return (
    <form onSubmit={handleSearch} onFocus={handleResetValidation}>
      <div className="grid grid-cols-12 mt-2 pb-4">
        <div className="col-span-3">
          <ErrorTooltip
            title={formValidations.departureRange || ''}
            open={formValidations.departureRange !== null}
            arrow
          >
            <input
              onClick={handleDepartureRangeClick}
              type="text"
              className={clsx(
                'bg-searchInput-0 px-6 py-3 rounded-l-2xl bg-white placeholder-gray-700 w-full h-full',
                formValidations.departureRange
                  ? 'border border-red-500 border-opacity-100'
                  : 'border border-gray-500 border-opacity-25',
              )}
              placeholder="Select departure range"
              readOnly
              value={
                fromDate && toDate
                  ? `${dayjs(fromDate).format('MMM D, YYYY')} - ${dayjs(toDate).format(
                      'MMM D, YYYY',
                    )}`
                  : ''
              }
            />
          </ErrorTooltip>
          <div className="absolute">
            <DateRangePicker
              toggle={() => setIsDepartureRangeDatepickerOpen(!isDepartureRangeDatepickerOpen)}
              open={isDepartureRangeDatepickerOpen}
              onChange={handleDepartureRangeChange}
              minDate={minDate}
              maxDate={maxDate}
              wrapperClassName={datePickerClasses.dateRangePicker}
              definedRanges={returnDefinedRanges()}
            />
          </div>
        </div>
        <div className="col-span-4 lg:col-span-5">
          <TwAutocomplete
            options={destinationsAutocompleteResults}
            placeholderText="Select destination(s)"
            onChange={handleDestinationsChange}
            classes={autocompleteClasses}
            values={locations}
            disableCloseOnSelect
            isMultiple
          />
        </div>
        <div className="col-span-3 lg:col-span-2">
          <DurationSearchInput />
        </div>
        <div className="col-span-2">
          <button
            type="submit"
            className="rounded-r-2xl bg-searchButton-0 text-searchbtn-0 text-sm font-bold text-center w-full h-full px-4"
          >
            Search
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-white">
        <FilterBar
          variant={variant}
          doShowAllFilters={doShowAllFilters}
          doDisablePassengersInput={doDisablePassengersInput}
          formValidations={formValidations}
        />
      </div>
    </form>
  );
};

CruiseSearchbar.propTypes = {
  variant: PropTypes.string,
  doShowAllFilters: PropTypes.bool,
  doDisablePassengersInput: PropTypes.bool,
};

CruiseSearchbar.defaultProps = {
  variant: undefined,
  doShowAllFilters: false,
  doDisablePassengersInput: false,
};
export default memo(CruiseSearchbar);
